import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, Save, Languages, Check, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const LOCALE_LABELS = {
  ru: '🇷🇺 Русский',
  en: '🇬🇧 English',
  es: '🇪🇸 Español',
  de: '🇩🇪 Deutsch',
  fr: '🇫🇷 Français',
  pt: '🇵🇹 Português',
  zh: '🇨🇳 中文',
};

/**
 * Russian-only editor for the referral invitation email template.
 * On save the backend auto-translates the Russian template to the other 6
 * locales (placeholders are protected and preserved).
 */
export default function AdminReferralEmailTemplate() {
  const [data, setData] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [bodyRef, setBodyRef] = useState(null);
  const [lastTranslate, setLastTranslate] = useState(null); // {ok:[], failed:[]}

  useEffect(() => {
    axios.get(`${API}/api/admin/email-templates/referral`, { headers: getAuthHeader() })
      .then(r => {
        setData(r.data);
        setSubject(r.data.ru.subject || '');
        setBody(r.data.ru.body_html || '');
      })
      .catch(() => toast.error('Не удалось загрузить шаблон'));
  }, []);

  if (!data) return <div style={{ padding: '1rem' }}>Загрузка шаблона…</div>;

  const insertPlaceholder = (ph) => {
    if (!bodyRef) {
      setBody(b => b + ph);
      return;
    }
    const start = bodyRef.selectionStart;
    const end = bodyRef.selectionEnd;
    const before = body.substring(0, start);
    const after = body.substring(end);
    const newVal = before + ph + after;
    setBody(newVal);
    setTimeout(() => {
      bodyRef.focus();
      bodyRef.selectionStart = bodyRef.selectionEnd = start + ph.length;
    }, 30);
  };

  const renderPreview = () => {
    let html = body;
    const sample = {
      '{name}': 'Иван',
      '{welcome}': '100',
      '{hints}': '3',
      '{hintWord}': 'подсказки',
      '{url}': 'https://countryscanner.ru/game/ref/abcd1234',
    };
    Object.entries(sample).forEach(([k, v]) => { html = html.split(k).join(v); });
    setPreviewHtml({ subject: subject.replace(/\{name\}/g, 'Иван'), html });
  };

  const save = async () => {
    setSaving(true);
    setLastTranslate(null);
    try {
      const r = await axios.put(
        `${API}/api/admin/email-templates/referral`,
        { subject, body_html: body },
        { headers: getAuthHeader() },
      );
      const ok = r.data.translated_ok || [];
      const failed = r.data.translated_failed || [];
      setLastTranslate({ ok, failed });
      if (failed.length === 0) {
        toast.success(`Шаблон сохранён и переведён на ${ok.length} языков`);
      } else {
        toast.warning(`Сохранено. Переведено: ${ok.length}, не удалось: ${failed.join(', ')}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const overlayHtml = (body || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\{url\}/g, '<mark class="ph-url">{url}</mark>')
    .replace(/\{name\}/g, '<mark class="ph-name">{name}</mark>')
    .replace(/\{welcome\}/g, '<mark class="ph-misc">{welcome}</mark>')
    .replace(/\{hints\}/g, '<mark class="ph-misc">{hints}</mark>')
    .replace(/\{hintWord\}/g, '<mark class="ph-misc">{hintWord}</mark>');

  return (
    <div data-testid="admin-referral-email-template" style={{ maxWidth: 980, padding: '0.5rem 0' }}>
      <style>{`
        .ret-grid { display: grid; grid-template-columns: minmax(0, 1fr) 280px; gap: 16px; }
        @media (max-width: 800px) { .ret-grid { grid-template-columns: 1fr; } }
        .ret-label { font-size: 12px; font-weight: 600; color: #64748b;
                     text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px; }
        .ret-input { width: 100%; padding: 10px 12px; border: 1px solid #cbd5e1;
                     border-radius: 8px; font-size: 14px; box-sizing: border-box; font-family: inherit; }
        .ret-body-wrap { position: relative; }
        .ret-body, .ret-overlay { width: 100%; min-height: 360px; padding: 12px;
                                  border: 1px solid #cbd5e1; border-radius: 8px;
                                  font-family: 'SF Mono', Menlo, Consolas, monospace;
                                  font-size: 13px; line-height: 1.55; box-sizing: border-box;
                                  white-space: pre-wrap; word-wrap: break-word; }
        .ret-body { position: relative; background: transparent; color: #0f172a;
                    resize: vertical; outline: none; }
        .ret-overlay { position: absolute; inset: 0; pointer-events: none; color: transparent;
                       background: white; z-index: 0; overflow: hidden; }
        .ret-overlay mark { background: transparent; color: transparent; padding: 0; }
        .ret-overlay .ph-url { background: rgba(34, 197, 94, 0.18); color: transparent;
                               outline: 1px solid rgba(34, 197, 94, 0.4); border-radius: 3px; }
        .ret-overlay .ph-name { background: rgba(59, 130, 246, 0.15); color: transparent;
                                outline: 1px solid rgba(59, 130, 246, 0.35); border-radius: 3px; }
        .ret-overlay .ph-misc { background: rgba(245, 158, 11, 0.15); color: transparent;
                                outline: 1px solid rgba(245, 158, 11, 0.35); border-radius: 3px; }
        .ret-body { position: relative; z-index: 1; background: transparent; }
        .ret-aside { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
                     padding: 12px; height: fit-content; }
        .ret-aside h4 { margin: 0 0 8px; font-size: 13px; font-weight: 700; color: #0f172a; }
        .ret-ph-btn { width: 100%; text-align: left; padding: 8px 10px; margin-bottom: 6px;
                      background: white; border: 1px solid #e2e8f0; border-radius: 8px;
                      cursor: pointer; font-size: 13px; transition: all 0.12s;
                      display: flex; justify-content: space-between; align-items: center; gap: 8px; }
        .ret-ph-btn:hover { border-color: #93c5fd; background: #eff6ff; }
        .ret-ph-key { font-family: monospace; font-weight: 700; color: #0a4797; }
        .ret-ph-key.url { color: #15803d; }
        .ret-ph-label { color: #64748b; font-size: 12px; }
        .ret-actions { display: flex; gap: 8px; margin-top: 12px; align-items: center; flex-wrap: wrap; }
        .ret-btn { padding: 9px 16px; border-radius: 8px; border: none; cursor: pointer;
                   font-size: 14px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
        .ret-btn-primary { background: #0a4797; color: white; }
        .ret-btn-primary:disabled { opacity: 0.65; cursor: wait; }
        .ret-btn-secondary { background: white; border: 1px solid #cbd5e1; color: #0f172a; }
        .ret-preview-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,0.55);
                                z-index: 10001; display: flex; align-items: center;
                                justify-content: center; padding: 1rem; }
        .ret-preview-modal { background: white; border-radius: 14px; max-width: 640px;
                             width: 100%; max-height: 86vh; overflow: auto; padding: 1.25rem; }
        .ret-preview-subject { font-size: 16px; font-weight: 600; color: #0f172a;
                               padding-bottom: 0.5rem; margin-bottom: 0.75rem;
                               border-bottom: 1px solid #e2e8f0; }
        .ret-translate-info { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px;
                              padding: 12px 14px; font-size: 13px; color: #1e3a8a;
                              display: flex; gap: 10px; align-items: flex-start; margin-top: 8px; line-height: 1.5; }
        .ret-translate-result { margin-top: 10px; padding: 10px 12px; border-radius: 8px;
                                font-size: 13px; }
        .ret-translate-result.ok { background: #ecfdf5; border: 1px solid #6ee7b7; color: #065f46; }
        .ret-translate-result.warn { background: #fef3c7; border: 1px solid #fcd34d; color: #92400e; }
        .ret-lang-chip { display: inline-block; padding: 2px 8px; border-radius: 999px;
                         background: white; border: 1px solid #cbd5e1; margin: 2px 4px 2px 0;
                         font-size: 12px; }
      `}</style>

      <div className="ret-translate-info">
        <Languages size={18} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          Редактируется только <b>русский</b> шаблон. На остальные 6 языков
          (en, es, de, fr, pt, zh) перевод выполняется автоматически при сохранении
          через Yandex Translate. Плейсхолдеры <code>{'{name}'}</code>, <code>{'{welcome}'}</code>,
          <code>{'{hints}'}</code>, <code>{'{hintWord}'}</code>, <code>{'{url}'}</code> защищены
          и не переводятся.
        </div>
      </div>

      <div className="ret-grid" style={{ marginTop: 14 }}>
        <div>
          <div className="ret-label">Тема письма (RU)</div>
          <input
            className="ret-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="ret-subject"
          />
          <div className="ret-label" style={{ marginTop: 14 }}>HTML тело письма (RU)</div>
          <div className="ret-body-wrap">
            <div className="ret-overlay" dangerouslySetInnerHTML={{ __html: overlayHtml }} />
            <textarea
              ref={setBodyRef}
              className="ret-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              spellCheck={false}
              data-testid="ret-body"
            />
          </div>
          <div className="ret-actions">
            <button className="ret-btn ret-btn-primary" onClick={save} disabled={saving} data-testid="ret-save">
              <Save size={16} /> {saving ? 'Сохраняем и переводим…' : 'Сохранить и перевести'}
            </button>
            <button className="ret-btn ret-btn-secondary" onClick={renderPreview} data-testid="ret-preview">
              <Eye size={16} /> Предпросмотр
            </button>
          </div>

          {lastTranslate && (
            lastTranslate.failed.length === 0 ? (
              <div className="ret-translate-result ok" data-testid="ret-translate-result">
                <Check size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Переведено на {lastTranslate.ok.length} языков:&nbsp;
                {lastTranslate.ok.map(l => (
                  <span className="ret-lang-chip" key={l}>{LOCALE_LABELS[l] || l}</span>
                ))}
              </div>
            ) : (
              <div className="ret-translate-result warn" data-testid="ret-translate-result">
                <AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Сохранено. Переведено: {lastTranslate.ok.length}.
                Не удалось перевести:&nbsp;
                {lastTranslate.failed.map(l => (
                  <span className="ret-lang-chip" key={l}>{LOCALE_LABELS[l] || l}</span>
                ))}
              </div>
            )
          )}
        </div>

        <aside className="ret-aside">
          <h4>Доступные плейсхолдеры</h4>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, lineHeight: 1.5 }}>
            Кликните, чтобы вставить в место курсора. <code>{'{url}'}</code> — реферальная ссылка
            (выделена <span style={{ background: 'rgba(34,197,94,0.18)', padding: '0 4px', borderRadius: 3 }}>зелёным</span>
            в редакторе).
          </div>
          {data.placeholders.map(p => (
            <button
              key={p.key}
              className="ret-ph-btn"
              onClick={() => insertPlaceholder(p.key)}
              data-testid={`ret-insert-${p.key.replace(/[{}]/g, '')}`}
            >
              <span className={`ret-ph-key${p.key === '{url}' ? ' url' : ''}`}>{p.key}</span>
              <span className="ret-ph-label">{p.label}</span>
            </button>
          ))}
        </aside>
      </div>

      {previewHtml && (
        <div className="ret-preview-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setPreviewHtml(null); }}>
          <div className="ret-preview-modal">
            <div className="ret-preview-subject">{previewHtml.subject}</div>
            <div dangerouslySetInnerHTML={{ __html: previewHtml.html }} />
            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button className="ret-btn ret-btn-secondary" onClick={() => setPreviewHtml(null)}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
